import {ValueOf} from 'next/constants'

import {Plugin} from 'chart.js'

import {tooltipWidth} from '$components/insurance/chart/radarTooltip'
import {REPORT_ID} from '$components/insurance/my/home/diagnosis/InsuranceCoverageCheck'
import {보장분류_키워드} from '$share/constants/insurance'
import {get또래대비보장ratio} from '$utils/insurance'

export const GRAPH_COLOR = {
    // 초록색
    main: {
        border: 'rgba(9, 171, 73, 1)',
        background: 'rgba(0, 222, 90, 1)',
    },
    // 하늘색
    background: {
        border: 'rgba(52, 170, 255, 1)',
        background: 'rgba(222, 238, 255, 1)',
    },
    // 연한 하늑색
    frontBackground: {
        background: 'rgba(222, 238, 255, 0)',
    },
}

// 들어온 값을 천만으로 정규화해서 사용
export const maxTicksLimit = 10_000_000
export const DATASET_LABEL = '내 보장'
export const BACKGROUND_LABEL = '또래 평균'
// 툴팁 우측 여백. 디자인 가이드
export const TOOLTIP_MARGIN = 14

const highlightLabels = [
    보장분류_키워드.실손의료.replace('의료', ''),
    보장분류_키워드.암,
    보장분류_키워드.뇌,
    보장분류_키워드.심장,
] as const

const LABEL_COLOR = {
    // 충분 / 91% ~ / 파랑
    enough: {
        text: 'rgba(0, 126, 255, 1)',
        background: 'rgba(222, 238, 255, 1)',
    },
    // 부족 / 50% ~ 90% / 노랑
    insufficient: {
        text: 'rgba(255, 138, 0, 1)',
        background: 'rgba(255, 248, 208, 1)',
    },
    // 없음 or 매우부족 / 0% ~ 49% / 빨강
    dangerous: {
        text: 'rgba(247, 54, 49, 1)',
        background: 'rgba(255, 234, 235, 1)',
    },
    // 기본 색상 회색
    default: {
        text: 'rgba(146, 146, 148, 1)',
        background: 'rgba(243, 245, 247, 1)',
    },
} as const

// 그래프 데이터 값에 따라 필요한 LABEL_COLOR 결정하는 함수
const getLabelColor = (labelText: string, labelData: number, backgroundData: number): ValueOf<typeof LABEL_COLOR> => {
    // 의료, 암, 뇌, 심장을 제외한 보장은 default 색상 처리
    if (!highlightLabels.includes(labelText)) {
        return LABEL_COLOR.default
    }

    const ratio = get또래대비보장ratio(labelData, backgroundData)

    if (ratio > 90) {
        return LABEL_COLOR.enough
    }

    if (ratio >= 50) {
        return LABEL_COLOR.insufficient
    }

    return LABEL_COLOR.dangerous
}

interface SetLabelProps {
    label: string
    ctx: CanvasRenderingContext2D
    color: ValueOf<typeof LABEL_COLOR>
    values: {
        adjustedX: number // 보정 x 좌표
        adjustedY: number // 보정 y 좌표
        labelPaddingX: number // 라벨 글자 좌우 여백
        labelPaddingY: number // 라벨 글자 상하 여백
        textWidth: number // 글자 너비
        textHeight: number // 글자 높이
        borderRadius: number
    }
}
// 그래프 바깥 label을 그리는 함수
const setLabel = ({label, ctx, color, values}: SetLabelProps) => {
    // 위치 계산에 필요한 값들
    const {adjustedX, adjustedY, labelPaddingX, labelPaddingY, textWidth, textHeight, borderRadius} = values

    // 배경 색상 설정
    ctx.fillStyle = color.background
    ctx.roundRect(
        adjustedX,
        adjustedY - textHeight - labelPaddingY / 2, // 위쪽 여백을 고려
        textWidth + labelPaddingX * 2,
        textHeight + labelPaddingY * 2,
        borderRadius,
    )
    // 배경 그리기
    ctx.fill()

    // 색상 설정
    ctx.fillStyle = color.text
    ctx.fillText(label, adjustedX + labelPaddingX, adjustedY) // 레이블 텍스트 그리기
}

export const labelFontSize = 11 // 레이블 글자 크기
export const labelPaddingX = 8 // 좌우 패딩
export const labelPaddingY = 4 // 상하 패딩
const borderRadius = 11 // 테두리의 둥근 모서리 반지름

// 12시 방향을 시작점으로 하는 각도를 구하는 함수. 삼각함수에서 사용
export const getAngle = ({index, length}: {index: number; length: number}) =>
    (index / length) * (Math.PI * 2) - Math.PI / 2

// label의 x,y좌표를 구하는 함수
export const getLabelPosition = ({
    centerX,
    centerY,
    radius,
    angle,
}: {
    centerX: number
    centerY: number
    radius: number
    angle: number
}) => {
    const x = centerX + radius * Math.cos(angle) // x 좌표
    const y = centerY + radius * Math.sin(angle) // y 좌표

    return {x, y}
}

/**
 * parent 노드와 canvas간에 padding top이 20px이 존재. 이 부분만 보정
 */
const PADDING_TOP = 20

/**
 * canvas의 상대 좌표로 변환
 * 툴팁이 노출되는 그래프 상단 노드중 position:relative인 노드를 기준으로 좌표를 환산
 * 부모 노드와 canvas간의 간격도 고려해야함
 * canvas에 요소를 그릴 때 사용
 */
export const getRelativeCanvasPosition = ({x, y, canvasWidth}: {x: number; y: number; canvasWidth: number}) => {
    const parentNode = document.getElementById(REPORT_ID)

    if (!parentNode) {
        return {x, y}
    }

    // viewport와 부모 노드간 상대좌표 계산을 위해 left, top 사용 (스크롤도 고려된 값)
    const {left, top, width} = parentNode.getBoundingClientRect()

    /**
     * canvas그래프의 최대 크기는 312px
     * parent 노드는 이보다 커질 수 있음. parent node와 canvas그래프 너비 차이만큼 x좌표를 보정해야 정확한 좌표값을 구할 수 있음
     */
    const marginLeft = (width - canvasWidth) / 2

    /**
     * x좌표와 달리 y좌표는 화면 크기에 따라 달라지는 부분이 없음
     * parent 노드와 canvas간에 padding top이 20px이 존재. 이 부분만 paddingTop 사용해 보정
     */

    return {
        // 절대 좌표 - 부모 노드 보정 - canvas 보정
        x: x - left - marginLeft,
        y: y - top - PADDING_TOP,
    }
}

/**
 * getRelativeCanvasPosition를 통해 구해진 canvas 상대좌표 값을 parent 기준 상대좌표로 변환
 * 툴팁의 좌표를 정할 때 사용. 툴팁은 parent node 기준으로 위치를 정하기 때문
 */
export const getCanvasToParentPosition = ({x, y, canvasWidth}: {x: number; y: number; canvasWidth: number}) => {
    const parentNode = document.getElementById(REPORT_ID)

    if (!parentNode) {
        return {x, y}
    }

    // viewport와 부모 노드간 간격 계산을 위해 부모 노드의 width 획득
    const {width} = parentNode.getBoundingClientRect()

    /**
     * canvas그래프의 최대 크기는 312px
     * parent 노드는 이보다 커질 수 있음. parent node와 canvas그래프 너비 차이만큼 x좌표를 보정해야 정확한 좌표값을 구할 수 있음
     */
    const marginLeft = (width - canvasWidth) / 2

    return {
        /**
         * getRelativeCanvasPosition 사용해 계산된 canvas 상대 좌표를 parent node 기준 상대좌표로 변환
         * canvas 보정 값을 제거해서 parent node 기준 상대좌표 변환
         */
        x: x + marginLeft,
        y: y + PADDING_TOP,
    }
}

/**
 * 화면 좌우 초과되는 경우 14px 여백을 남기고 노출될 수 있도록 보정합니다.
 */
export const getWindowCalculatedLeft = (x: number) => {
    const parentNode = document.getElementById(REPORT_ID)

    if (!parentNode) {
        return x
    }

    const {left} = parentNode.getBoundingClientRect()

    const absoluteLeft = x + left

    // 툴팁의 오른쪽이 화면 오른쪽 margin 14px을 넘어갈 경우
    if (absoluteLeft + tooltipWidth > window.innerWidth - TOOLTIP_MARGIN) {
        // 최대 오른쪽 가능 위치의 parent 상대 좌표
        return window.innerWidth - TOOLTIP_MARGIN - left - tooltipWidth
    }

    // 툴팁의 왼쪽이 화면 왼쪽 margin 14px을 넘어간 경우
    if (absoluteLeft < TOOLTIP_MARGIN) {
        return TOOLTIP_MARGIN - left
    }

    // 왼쪽, 오른쪽 margin 14px을 모두 침범하지 않는 경우 그대로 사용
    return x
}

export const labelDrawPlugin: Plugin = {
    id: 'custom_label_color',
    beforeDraw: (chart) => {
        const {ctx, chartArea} = chart
        ctx.save()

        CanvasRenderingContext2D.prototype.roundRect = function (
            x: number,
            y: number,
            width: number,
            height: number,
            radius: number,
        ) {
            this.beginPath()
            this.moveTo(x + radius, y)
            this.lineTo(x + width - radius, y)
            this.quadraticCurveTo(x + width, y, x + width, y + radius)
            this.lineTo(x + width, y + height - radius)
            this.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
            this.lineTo(x + radius, y + height)
            this.quadraticCurveTo(x, y + height, x, y + height - radius)
            this.lineTo(x, y + radius)
            this.quadraticCurveTo(x, y, x + radius, y)
            this.closePath()
        }

        const labelOffset = chartArea.width * 0.12 // 레이블과 차트 간의 추가 거리. **휴리스틱한 값 적용**

        // 레이블의 각도와 반지름 계산
        const labels = chart.data.labels as string[]

        const radius = (Math.min(chartArea.width, chartArea.height) / 2) * 0.9 // 차트 반지름. **휴리스틱한 값 적용**
        const centerX = chartArea.left + chartArea.width / 2 // 차트 중앙 x 좌표
        const centerY = chartArea.top + chartArea.height / 2 // 차트 중앙 y 좌표

        ctx.font = `${labelFontSize}px Arial`

        const datasets = chart.data.datasets.filter((elem) => elem.label === DATASET_LABEL)
        const backgroundDatasets = chart.data.datasets.filter((elem) => elem.label === BACKGROUND_LABEL)

        // 그래프 데이터
        const labelDataSets = (
            datasets.length > 0 && datasets[0].data.length === labels.length ? datasets[0].data : []
        ) as number[]
        const labelBackgroundDataSets = (
            backgroundDatasets.length > 0 && backgroundDatasets[0].data.length === labels.length
                ? backgroundDatasets[0].data
                : []
        ) as number[]

        labels.forEach((label, index) => {
            // 각도 계산 (8개의 레이블을 가정)
            const angle = getAngle({index, length: labels.length})
            // 레이블 위치 계산
            const {x, y} = getLabelPosition({centerX, centerY, radius: radius + labelOffset, angle})

            // 레이블 위치 보정: 글자 크기와 여백을 반영
            const textWidth = ctx.measureText(label).width // 텍스트의 너비
            const textHeight = labelFontSize // 텍스트 높이

            // 배경 그리기
            const isLeft = index > labels.length / 2
            const adjustedX = x - textWidth / 2 - (isLeft ? labelPaddingX * 1.5 : labelPaddingX) // x 좌표 보정 **휴리스틱한 값 적용**
            let adjustedY = y + textHeight / 2 - (isLeft ? labelPaddingY : labelPaddingY / 2) // y 좌표 보정 **휴리스틱한 값 적용**

            // 12시방향 보정
            if (index === 0) {
                adjustedY += labelPaddingY
            }

            // 6시 정방향 보정 (정방향 아닌경우 불필요)
            if (index === labels?.length / 2) {
                adjustedY -= labelPaddingY
            }

            setLabel({
                label,
                ctx,
                color: getLabelColor(label, labelDataSets[index], labelBackgroundDataSets[index]),
                values: {adjustedX, adjustedY, labelPaddingX, labelPaddingY, textWidth, textHeight, borderRadius},
            })
        })

        ctx.restore()
    },
}

// index별 y좌표 보정 값. 휴리스틱
export const getTooltipTopByIndex = (index: number) => {
    const 라벨_툴팁_간격 = 8 // 디자인에서 정의한 간격 8px

    switch (index) {
        case 0:
            return 7 + 라벨_툴팁_간격
            break
        case 1:
            return 6 + 라벨_툴팁_간격
            break
        case 2:
            return 10.5 + 라벨_툴팁_간격
            break
        case 3:
            return -105 - 라벨_툴팁_간격
            break
        case 4:
            return -107 - 라벨_툴팁_간격
            break
        case 5:
            return 8 + 라벨_툴팁_간격
            break
        case 6:
            return 2 + 라벨_툴팁_간격
            break
        default:
            return 0
    }
}

// index별 x좌표 보정 값. 휴리스틱
export const getTooltipLeftByIndex = (index: number) => {
    switch (index) {
        case 0:
            return 0
            break
        case 1:
            return 3
            break
        case 2:
            return 4
            break
        case 3:
            return 3
            break
        case 4:
            return -7
            break
        case 5:
            return -9
            break
        case 6:
            return -8
            break
        default:
            return 0
    }
}

/**
 * debug 용도로 사용!!
 * x,y 좌표에 점을 찍습니다
 * canvas기준 상대 좌표여야 정확한 경로를 포인트합니다
 */
export const drawPoint = (x: number, y: number, color: string, ctx?: CanvasRenderingContext2D) => {
    if (!ctx) {
        return
    }

    ctx.beginPath()
    ctx.arc(x, y, 5, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
}
